import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Sparkles, Loader2, Trash2, Calendar, Tag, PoundSterling, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AppNav from '@/components/AppNav';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import FoldersTab from '@/components/FoldersTab';
import GuidedTour from '@/components/GuidedTour';
import AppShell from '@/components/AppShell';

const API = process.env.REACT_APP_BACKEND_URL;

const TOUR_KEY = 'poettik_dashboard_tour_v1_done';
const TOUR_STEPS = [
  {
    selector: '[data-testid="shell-new-script"]',
    title: 'Generate a script',
    body: 'Jump straight in — describe any product and Poettik writes the hooks, a ready-to-film script and filming angles for you.',
  },
  {
    selector: '[data-testid="new-product-button"]',
    title: 'Organise by product',
    body: 'Give each product its own workspace. Keep its winning videos, patterns and every generated script together in one place.',
  },
  {
    selector: '[data-testid="shell-nav-history"]',
    title: 'All your history',
    body: 'Every script you generate lands here for 14 days, so you can revisit, copy or make a voiceover any time.',
  },
];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  if (days === 0) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString('en-GB');
};

const GenerationCard = ({ gen, onDelete }) => {
  const [open, setOpen] = useState(false);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Card className="overflow-hidden" data-testid={`generation-card-${gen.id}`}>
      <SEO title="Dashboard" description="Your recent Poettik generations and content history." noindex />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-muted/30 transition-colors"
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-base truncate">{gen.product_name}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{gen.category}</span>
            <span className="flex items-center gap-1"><PoundSterling className="w-3 h-3" />{gen.price}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(gen.generated_at)}</span>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-border/50">
          {gen.text_hooks?.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">Text hooks</h4>
              <ul className="space-y-1.5">
                {gen.text_hooks.map((h, i) => (
                  <li key={i} className="text-sm flex justify-between gap-2">
                    <span className="flex-1">{h}</span>
                    <button onClick={() => copy(h)} className="text-xs text-primary hover:underline shrink-0">Copy</button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {gen.visual_hooks?.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">Visual hooks</h4>
              <ul className="space-y-2">
                {gen.visual_hooks.map((v, i) => (
                  <li key={i} className="text-sm">
                    <p className="font-medium">{v.hook}</p>
                    {v.why_it_works && <p className="text-xs text-muted-foreground mt-0.5">Why: {v.why_it_works}</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {gen.verbal_hooks?.length > 0 && (
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">Verbal hooks</h4>
              <ul className="space-y-1.5">
                {gen.verbal_hooks.map((h, i) => (
                  <li key={i} className="text-sm flex justify-between gap-2">
                    <span className="flex-1">{h}</span>
                    <button onClick={() => copy(h)} className="text-xs text-primary hover:underline shrink-0">Copy</button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {gen.script && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-primary">Script</h4>
                <button onClick={() => copy(gen.script)} className="text-xs text-primary hover:underline">Copy script</button>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{gen.script}</p>
            </section>
          )}

          <div className="pt-2">
            <Button
              size="sm"
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
              onClick={(e) => { e.stopPropagation(); onDelete(gen.id); }}
              data-testid={`delete-generation-${gen.id}`}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

const DashboardPage = () => {
  const { isAuthenticated, getAuthHeader, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Products (default) vs History — driven by the ?tab=history query param so
  // the sidebar links switch views without a full remount.
  const tab = location.search.includes('history') ? 'history' : 'folders';
  const [runTour, setRunTour] = useState(false);

  // Kick off the first-use guided tour once, shortly after mount so the
  // dashboard content (folders tab) has rendered its targets.
  useEffect(() => {
    if (!isAuthenticated) return;
    let done = false;
    try { done = localStorage.getItem(TOUR_KEY) === '1'; } catch { /* ignore */ }
    if (done) return;
    const t = setTimeout(() => setRunTour(true), 600);
    return () => clearTimeout(t);
  }, [isAuthenticated]);

  const finishTour = () => {
    setRunTour(false);
    try { localStorage.setItem(TOUR_KEY, '1'); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/generations/recent`, { headers: getAuthHeader() });
        setItems(res.data.items || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load your recent generations');
      } finally { setLoading(false); }
    })();
  }, [isAuthenticated, getAuthHeader]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this generation? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/generations/${id}`, { headers: getAuthHeader() });
      setItems((prev) => prev.filter((g) => g.id !== id));
      toast.success('Generation deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  return (
    <AppShell title={tab === 'history' ? 'History' : 'Products'}>
      <main data-testid="dashboard-page" className="max-w-6xl w-full mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
        {tab === 'folders' && <FoldersTab />}

        {tab === 'history' && (
          <>
            <div className="mb-8">
              <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#06B6D4] mb-2">History</p>
              <h1 className="font-display text-[32px] leading-none font-semibold text-[#111111]">
                Your scripts
              </h1>
              <p className="text-sm text-[#6B6B6B] mt-2">
                Every script you&apos;ve generated recently, in one place.
              </p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-24" data-testid="dashboard-empty">
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#9A9A9A] mb-3">No scripts yet</p>
                <p className="text-[#6B6B6B] mb-6">Generate your first script and it&apos;ll appear here.</p>
                <Link to="/generator">
                  <button className="inline-flex items-center gap-2 h-11 px-5 rounded-[12px] bg-[#111111] text-white text-sm font-medium hover:-translate-y-0.5 transition-transform">
                    New script
                  </button>
                </Link>
              </div>
            ) : (
              <>
                <div className="space-y-3" data-testid="dashboard-items">
                  {items.map((g) => (
                    <GenerationCard key={g.id} gen={g} onDelete={handleDelete} />
                  ))}
                </div>
                <p className="text-xs text-[#9A9A9A] mt-8" data-testid="history-retention-note">
                  Scripts are kept for 14 days.
                </p>
              </>
            )}
          </>
        )}
      </main>

      <GuidedTour steps={TOUR_STEPS} open={runTour} onDone={finishTour} />
    </AppShell>
  );
};

export default DashboardPage;
